import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { ApiTableModel } from 'components/ApiTable/model/ApiTableModel';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { Button } from 'components/Button/Button';
import { FormikDatePickerField } from 'components/FormikDatePickerField/FormikDatePickerField';
import { FormikMultiSelectField } from 'components/FormikMultiSelectField/FormikMultiSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { FormikToggleField } from 'components/FormikToggleField/FormikToggleField';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { type FormikErrors, useFormik } from 'formik';
import { useApiTokenRolesOptions } from 'pages/Settings/Integration/ApiTokens/useApiTokenRolesOptions';
import type React from 'react';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import deepEqual from 'react-fast-compare';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { useNotifications } from 'utils/hooks/useNotifications';
import { type ApiHook, useApi } from 'utils/request/useApi';

export type EditedApiToken = {
  id?: string;
  description: string;
  expirationEnabled: boolean;
  expires: string | null;
  roleIds: string[];
};

type Props =
  | {
      type: 'create';
      initialValues: undefined;
      dateFormat: string;
      onClose: () => void;
      getApiTableModel: () => ApiTableModel;
      setSavedToken: Dispatch<SetStateAction<string | null>>;
    }
  | {
      type: 'edit';
      initialValues: EditedApiToken;
      dateFormat: string;
      onClose: () => void;
      getApiTableModel: () => ApiTableModel;
    };

export type ApiTokenFormValues = {
  id?: string;
  description: string;
  expirationEnabled: boolean;
  expires: string | null;
  roleIds: string[];
};

const createApiToken = async ({
  values,
  api,
}: { values: ApiTokenFormValues; api: ApiHook }) => {
  return await api.request({
    url: '/api-tokens',
    method: 'POST',
    data: {
      description: values.description,
      roleIds: values.roleIds,
      expires:
        values.expirationEnabled && values.expires
          ? new Date(values.expires).getTime()
          : null,
    },
  });
};

const editApiToken = async ({
  values,
  api,
}: { values: ApiTokenFormValues; api: ApiHook }) =>
  await api.request({
    url: `/api-tokens/${values.id}`,
    method: 'PUT',
    data: {
      description: values.description,
      roleIds: values.roleIds,
      expires: values.expirationEnabled
        ? values.expires
          ? new Date(values.expires).getTime()
          : null
        : null,
    },
  });

export const ApiTokenDialog: React.FC<Props> = (props) => {
  const [submitting, setSubmitting] = useLoadingState();
  const api = useApi();
  const notifications = useNotifications();

  const handleSubmit = async (values: ApiTokenFormValues) => {
    await withLoadingIndicator(setSubmitting)(async () => {
      if (props.type === 'create') {
        const response = await createApiToken({ values, api });
        notifications.showNotification({
          type: 'success',
          message: 'Api Token created',
        });
        props.setSavedToken(response.token);
        props.getApiTableModel().dispatch(reloadTableData);
        props.onClose();
      }
      if (props.type === 'edit') {
        await editApiToken({ values, api });
        notifications.showNotification({
          type: 'success',
          message: 'Api Token updated',
        });
      }

      props.getApiTableModel().dispatch(reloadTableData);
      props.onClose();
    });
  };

  const formik = useFormik({
    initialValues: {
      id: props.type === 'create' ? undefined : props.initialValues.id,
      description:
        props.type === 'create' ? '' : props.initialValues.description,
      expirationEnabled:
        props.type === 'create' ? false : props.initialValues.expirationEnabled,
      expires: props.type === 'create' ? null : props.initialValues.expires,
      roleIds: props.type === 'create' ? [] : props.initialValues.roleIds,
    },
    validate: (values: ApiTokenFormValues) => {
      const errors: FormikErrors<ApiTokenFormValues> = {};
      if (!values.description) {
        errors.description = 'Description is required';
      }
      if (values.roleIds.length === 0) {
        errors.roleIds = 'Roles are required';
      }
      return errors;
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
    enableReinitialize: true,
  });

  const rolesOptions = useApiTokenRolesOptions();

  const changed = useMemo(
    () => !deepEqual(formik.initialValues, formik.values),
    [formik.initialValues, formik.values],
  );

  return (
    <OverlayModal
      onClose={props.onClose}
      contentClassName='add-path-verification'
    >
      <OverlayModalHeader>
        <h4>
          {props.type === 'create' ? 'Create API token' : 'Edit API token'}
        </h4>
      </OverlayModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <OverlayModalBody>
          <FormikTextField
            autoComplete='off'
            name='description'
            formik={formik}
            type='text'
            label={'Description'}
          />
          <div className='form__label'>Expiration</div>
          <div className='flex items-center'>
            <FormikToggleField
              name='expirationEnabled'
              formik={formik}
              messages={{ on: '', off: '' }}
            />
            {formik.values.expirationEnabled ? (
              <FormikDatePickerField
                dateFormat={props.dateFormat}
                name='expires'
                formik={formik}
                placeholderText='Select date'
                disabledBeforeToday
              />
            ) : (
              <span>Never</span>
            )}
          </div>
          <FormikMultiSelectField
            name='roleIds'
            formik={formik}
            searchable
            label={'Roles'}
            loading={rolesOptions === null}
            options={rolesOptions ?? []}
            placeholder='Select roles'
          />
        </OverlayModalBody>
        <OverlayModalFooter>
          <Button
            disabled={!changed}
            loading={submitting === 'LOADING'}
            type='submit'
            variant='primary'
            size='sm'
            icon='fa-floppy-disk'
          >
            {props.type === 'create' ? 'Create' : 'Update'}
          </Button>
        </OverlayModalFooter>
      </form>
    </OverlayModal>
  );
};
